/* eslint-disable */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaExclamationTriangle, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import AccessToken from '../components/accessToken/accessToken';
import { endPoints } from '../api/endpoint';
import {
    Box,
    Button,
    Link,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import {
    clusterContainerStyle,
    clusterTitleStyle,
    paperStyleIns,
    tableStyle,
    thStyle,
    tdStyle,
    sortableCellStyle,
    getBadgeStyle,
    popupOverlayStyle,
    popupContentStyle,
    popupTitleStyle,
    zoomModalStyle,
    zoomContentStyle,
    zoomImageStyle,
    imageGridStyle,
    imageContainerStyle,
    imageStyle,
    imageButtonStyle,
    downloadButtonStyle,
    imageNameStyle,
    linkButtonStyle,
    disabledLinkButtonStyle,
    loadingTextStyle,
    subtitleStyle,
    closeButtonStyle,
    paginationContainerStyle,
    zoomControlsStyle,
    badgeIconStyle,
    paperStyle,
    tablePaginationStyle,
    downloadLinkStyle,
    noImagesTextStyle,
} from './style';

const ClusterDetailsTable = ({ clusterId }) => {
    const [tableData, setTableData] = useState(null);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const { getAccessToken, handleTokenError } = AccessToken();
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [zoomIndex, setZoomIndex] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(4);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedClusters = (clusters) => {
        if (!clusters) return [];
        const prioritySorted = [...clusters].sort((a, b) => {
            const weightA = a.sparse === 'critical' ? 0 : 1;
            const weightB = b.sparse === 'critical' ? 0 : 1;
            return weightA - weightB; // critical → normal
        });
        if (!sortConfig.key) return prioritySorted;
        return [...prioritySorted].sort((a, b) => {
            let valA = a[sortConfig.key];
            let valB = b[sortConfig.key];
            if (sortConfig.key === 'drifted') {
                valA = a.drifted ? 'Yes' : 'No';
                valB = b.drifted ? 'Yes' : 'No';
            }
            if (typeof valA === 'string' && typeof valB === 'string') {
                return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        });
    };
    // Helper: get image URL from the new response structure
    const getImageUrlAt = (idx) => {
        const img = images[idx];
        if (!img) return '';
        if (typeof img === 'string') return img;
        return img.url || img.image_url || img.path || '';
    };

    // Keyboard handling for Escape and arrow navigation
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (zoomIndex !== null) {
                    setZoomIndex(null);
                } else if (showPopup) {
                    setShowPopup(false);
                }
            } else if (e.key === 'ArrowRight' && zoomIndex !== null) {
                setZoomIndex((i) => (i + 1) % images.length);
            } else if (e.key === 'ArrowLeft' && zoomIndex !== null) {
                setZoomIndex((i) => (i - 1 + images.length) % images.length);
            }
        };

        if (showPopup || zoomIndex !== null) {
            window.addEventListener('keydown', onKey);
        }
        return () => window.removeEventListener('keydown', onKey);
    }, [showPopup, zoomIndex, images.length]);

    // Fetch table details
    const fetchTableDetails = async () => {
        if (!clusterId?.model_id) return;

        setLoading(true);
        try {
            const accessToken = await getAccessToken();
            const response = await axios.get(endPoints.getClusterTableDetails(clusterId?.model_id), { headers: { Authorization: `Bearer ${accessToken}` } });
            setTableData(response?.data);
        } catch (error) {
            console.error('Failed to fetch table details', error);
            handleTokenError(error, endPoints.getClusterTableDetails(clusterId?.model_id), 'get');
        } finally {
            setLoading(false);
        }
    };

    // Fetch images for a specific cluster
    const fetchClusterImages = async (label) => {
        if (!clusterId?.model_id) return;

        setImagesLoading(true);
        setSelectedLabel(label);
        try {
            const accessToken = await getAccessToken();
            const response = await axios.get(endPoints.getClusterImages(clusterId?.model_id, label), { headers: { Authorization: `Bearer ${accessToken}` } });

            const imagesData = response?.data?.data?.image_details || [];
            setImages(Array.isArray(imagesData) ? imagesData : []);
            setShowPopup(true);
            setCurrentPage(0);
        } catch (error) {
            console.error('Failed to fetch cluster images', error);
            handleTokenError(error, endPoints.getClusterImages(clusterId?.model_id, label), 'get');
        } finally {
            setImagesLoading(false);
        }
    };

    useEffect(() => {
        if (clusterId?.model_id) {
            fetchTableDetails();
        }
    }, [clusterId?.model_id]);

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Calculate paginated clusters
    const getPaginatedClusters = () => {
        if (!tableData?.data?.clusters) return [];
        const clusters = tableData.data.clusters;
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const sortedClusters = getSortedClusters(clusters);
        return sortedClusters.slice(startIndex, endIndex);
    };

    const renderTableContent = () => {
        if (loading) {
            return <p style={loadingTextStyle}>Loading cluster details...</p>;
        }

        if (!tableData) {
            return <p style={loadingTextStyle}>No cluster data available.</p>;
        }

        const paginatedClusters = getPaginatedClusters();
        const totalClusters = tableData?.data?.clusters?.length || 0;

        if (paginatedClusters.length === 0) {
            return <p style={loadingTextStyle}>No clusters data available.</p>;
        }

        return (
            <Paper elevation={2} sx={paperStyleIns}>
                <TableContainer>
                    <Table style={tableStyle}>
                        <TableHead sx={{ backgroundColor: (theme) => theme.palette.cyan['90'] }}>
                            <TableRow>
                                <TableCell
                                    style={sortableCellStyle}
                                    onClick={() => handleSort('cluster_label')}
                                    title="Unique identifier for this group of similar images."
                                >
                                    Cluster ID{' '}
                                    {sortConfig.key === 'cluster_label' ? sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown /> : <FaSort />}
                                </TableCell>

                                <TableCell
                                    style={sortableCellStyle}
                                    onClick={() => handleSort('drifted')}
                                    title="New Pattern = production images don't match any training data pattern"
                                >
                                    Drift Status {sortConfig.key === 'drifted' ? sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown /> : <FaSort />}
                                </TableCell>

                                <TableCell
                                    style={sortableCellStyle}
                                    onClick={() => handleSort('sparse')}
                                    title="Low Coverage = very few images in this group, may indicate a data gap"
                                >
                                    Data Coverage {sortConfig.key === 'sparse' ? sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown /> : <FaSort />}
                                </TableCell>

                                <TableCell
                                    style={sortableCellStyle}
                                    onClick={() => handleSort('production_percent')}
                                    title="What % of all production images fall in this cluster"
                                >
                                    Share in Production
                                    {sortConfig.key === 'production_percent' ? sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown /> : <FaSort />}
                                </TableCell>

                                <TableCell
                                    style={sortableCellStyle}
                                    onClick={() => handleSort('baseline_percent')}
                                    title="The share of the most similar training cluster — a reference point, not a before/after comparison"
                                >
                                    Nearest Training Match
                                    {sortConfig.key === 'baseline_percent' ? sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown /> : <FaSort />}
                                </TableCell>

                                <TableCell style={thStyle}>Explore</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedClusters.map((cluster, index) => (
                                <TableRow key={index}>
                                    <TableCell style={tdStyle}>{cluster.cluster_label}</TableCell>
                                    <TableCell style={tdStyle}>{cluster.drifted ? 'Yes' : 'No'}</TableCell>

                                    {/* Sparsity badge */}
                                    <TableCell style={tdStyle}>
                                        <span style={getBadgeStyle('sparsity', cluster.sparse === 'normal' ? 'Normal' : 'Critical')}>
                                            {cluster.sparse !== 'normal' && <FaExclamationTriangle style={badgeIconStyle} />}
                                            {cluster.sparse === 'normal' ? 'Normal' : 'Critical'}
                                        </span>
                                    </TableCell>

                                    <TableCell style={tdStyle}>{cluster.production_percent}%</TableCell>
                                    <TableCell style={tdStyle}>{cluster.baseline_percent}%</TableCell>
                                    <TableCell style={tdStyle}>
                                        <button
                                            type="button"
                                            onClick={() => fetchClusterImages(cluster.cluster_label)}
                                            disabled={imagesLoading}
                                            style={imagesLoading ? disabledLinkButtonStyle : linkButtonStyle}
                                        >
                                            {imagesLoading && selectedLabel === cluster.cluster_label ? 'Loading...' : 'Details'}
                                        </button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Material-UI TablePagination */}
                <TablePagination
                    rowsPerPageOptions={tablePaginationStyle.rowsPerPageOptions}
                    component="div"
                    count={totalClusters}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        );
    };

    // when thumbnail is clicked: open zoom at the correct absolute index
    const openZoomAt = (absoluteIndex) => {
        setZoomIndex(absoluteIndex);
    };

    const renderSummary = () => {
        const summary = tableData?.data?.summary;
        if (!summary) return null;
        const driftedColor = '#e57373';
        const sparseColor = '#f5a623';
        const cards = [
            {
                title: 'Total clusters',
                value: summary?.total,
                valueColor: '#333',
                dot: '#888',
                desc: 'Clusters analyzed this run',
            },
            {
                title: 'High Sparsity',
                value: summary?.sparse,
                valueColor: sparseColor,
                dot: sparseColor,
                desc: 'Clusters with very few images, potential data gaps',
            },
            {
                title: 'Drifted',
                value: summary?.drifted,
                valueColor: driftedColor,
                dot: driftedColor,
                desc: 'Production patterns not seen in training data',
            },
        ];
        return (
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 2,
                    mt: 1,
                    flexWrap: 'wrap',
                }}
            >
                {cards.map((card) => (
                    <Box
                        key={card.title}
                        sx={{
                            flex: '1 1 180px',
                            background: '#fff',
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            padding: '16px 20px',
                            boxShadow: '0 1px 4px rgba(0,92,188,0.07)',
                        }}
                    >
                        <Typography sx={{ fontSize: '13px', color: '#666', fontWeight: 500, mb: 0.5 }}>{card.title}</Typography>
                        <Typography sx={{ fontSize: '36px', fontWeight: 700, color: card.valueColor, lineHeight: 1.2, mb: 1 }}>{card.value}</Typography>
                        <Typography sx={{ fontSize: '12px', color: card.dot, display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <span
                                style={{
                                    marginTop: '3px',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: card.dot,
                                    flexShrink: 0,
                                    display: 'inline-block',
                                }}
                            />
                            {card.desc}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <div style={clusterContainerStyle}>
            <div style={clusterTitleStyle}>
                Data Insights{' '}
                <span style={subtitleStyle}>(Gain deeper insights into data quality, distribution, and drift to enable smarter model training.)</span>
            </div>

            {renderSummary()}
            {renderTableContent()}

            {/* Images popup (thumbnails + pagination) */}
            {showPopup && (
                <div
                    style={popupOverlayStyle}
                    role="dialog"
                    aria-modal="true"
                    onClick={() => {
                        setShowPopup(false);
                        setZoomIndex(null);
                    }}
                >
                    <div style={popupContentStyle} onClick={(e) => e.stopPropagation()} role="document">
                        <h3 style={popupTitleStyle}>Cluster {selectedLabel} Images</h3>

                        {images.length > 0 ? (
                            <>
                                {/* Image Grid */}
                                <div style={imageGridStyle}>
                                    {images.slice(currentPage * 12, currentPage * 12 + 12).map((img, idx) => {
                                        const absoluteIndex = currentPage * 12 + idx;
                                        const imageUrl = getImageUrlAt(absoluteIndex);
                                        const imageName = img.image_name || `Image-${absoluteIndex + 1}`;

                                        return (
                                            <div key={absoluteIndex} style={imageContainerStyle}>
                                                <button
                                                    type="button"
                                                    onClick={() => openZoomAt(absoluteIndex)}
                                                    style={imageButtonStyle}
                                                    aria-label={`Open ${imageName}`}
                                                >
                                                    <img src={imageUrl} alt={imageName} style={imageStyle} />
                                                </button>

                                                {/* Download Button */}
                                                <a
                                                    href={imageUrl}
                                                    download={imageName}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={downloadButtonStyle}
                                                    aria-label={`Download ${imageName}`}
                                                >
                                                    ⬇
                                                </a>

                                                {/* Image Name Tooltip */}
                                                <div style={imageNameStyle} title={imageName}>
                                                    {imageName}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Image Pagination */}
                                <Box sx={paginationContainerStyle}>
                                    <Button
                                        variant="contained"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => setCurrentPage((prev) => (prev > 0 ? prev - 1 : Math.ceil(images.length / 12) - 1))}
                                    >
                                        Prev
                                    </Button>

                                    <Typography variant="body1">
                                        Page {currentPage + 1} / {Math.ceil(images.length / 12) || 1}
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => setCurrentPage((prev) => (prev < Math.ceil(images.length / 12) - 1 ? prev + 1 : 0))}
                                    >
                                        Next
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <p style={noImagesTextStyle}>No images available for Cluster {selectedLabel}.</p>
                        )}
                        <IconButton
                            onClick={() => {
                                setShowPopup(false);
                                setZoomIndex(null);
                            }}
                            size="large"
                            sx={closeButtonStyle}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>
            )}

            {/* Zoom modal */}
            {zoomIndex !== null && getImageUrlAt(zoomIndex) && (
                <div role="dialog" aria-modal="true" style={zoomModalStyle} onClick={() => setZoomIndex(null)}>
                    <div onClick={(e) => e.stopPropagation()} style={zoomContentStyle}>
                        <img src={getImageUrlAt(zoomIndex)} alt={`Preview ${zoomIndex + 1}`} style={zoomImageStyle} />

                        <Box sx={zoomControlsStyle}>
                            <Button
                                variant="contained"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => setZoomIndex((i) => (i - 1 + images.length) % images.length)}
                            >
                                Prev
                            </Button>
                            <Link href={getImageUrlAt(zoomIndex)} download underline="none" sx={downloadLinkStyle}>
                                <Button variant="contained" startIcon={<DownloadIcon />}>
                                    Download
                                </Button>
                            </Link>
                            <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => setZoomIndex((i) => (i + 1) % images.length)}>
                                Next
                            </Button>
                            <Button variant="contained" color="error" endIcon={<CloseIcon />} onClick={() => setZoomIndex(null)}>
                                Close
                            </Button>
                        </Box>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClusterDetailsTable;
